import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { isNotBlank } from '@bizzdesign/bizzdesign-sdk';
import {
  buildNlsAttributeUpdate,
  buildNlsOverviewView,
  type AttributeValueWithParameterKey,
  type NlsFeedback,
  type NlsOverviewSegmentView,
  type NlsQueryInteractiveOverview,
  type SimpleValueWithParameterKey,
} from '@bizzdesign/data-access-search';
import type {
  AttributeDataPayload,
  BDTreeNode,
} from '@bizzdesign/shared/models';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { RenderMarkdownPipe } from '@bizzdesign/shared/pipes';
import { LangfuseFeedbackService } from '@bizzdesign/shared/services';
import { noMarginDivider } from '@bizzdesign/styles';
import { animate, style, transition, trigger } from '@angular/animations';
import { BadgeComponent } from '@bizzdesign/ui-badge';
import { HelperTextComponent } from '@bizzdesign/ui-helper-text';
import type { DividerDesignTokens } from '@primeuix/themes/types/divider';
import { QuillViewHTMLComponent } from 'ngx-quill';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { ValueEditorComponent } from '../value-editor/value-editor.component';
import { NlsOverviewAndFeedbackComponentStore } from './nls-overview-and-feedback.component.store';
import { InputTextModule } from 'primeng/inputtext';

type OverviewInput = string | NlsQueryInteractiveOverview | null | undefined;

@Component({
  selector: 'bd-nls-overview-and-feedback',
  standalone: true,
  templateUrl: './nls-overview-and-feedback.component.html',
  imports: [
    SharedI18nModule,
    DividerModule,
    ButtonModule,
    BadgeComponent,
    FormsModule,
    InputTextModule,
    HelperTextComponent,
    RenderMarkdownPipe,
    QuillViewHTMLComponent,
    ValueEditorComponent,
  ],
  providers: [NlsOverviewAndFeedbackComponentStore],
  styleUrls: ['./nls-overview-and-feedback.component.scss'],
  animations: [
    // Expand only. Collapsing to a pill means the card's width, padding and
    // radius all change at once, and its width target depends on this body
    // still being mounted — animating that always lands as separate steps, so
    // the collapse is left to happen in one frame.
    trigger('bdNlsOverviewBody', [
      transition(':enter', [
        style({
          overflow: 'hidden',
          opacity: 0,
          height: 0,
          marginTop: 0,
          marginBottom: 0,
        }),
        animate(
          '300ms ease-out',
          style({
            opacity: 1,
            height: '*',
            marginTop: '*',
            marginBottom: '*',
          }),
        ),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NlsOverviewAndFeedbackComponent {
  // Angular animations run regardless of the OS setting, so the trigger is
  // switched off for anyone who asked for reduced motion.
  protected readonly reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  readonly store = inject(NlsOverviewAndFeedbackComponentStore);
  private readonly feedbackService = inject(LangfuseFeedbackService);
  noMarginDividerDesignTokens: DividerDesignTokens = noMarginDivider;

  // Plain markdown string (filter mode) or parsed interactive array
  // (cypher mode). Both shapes render.
  overview = input<OverviewInput>(null);

  // When true, interactive parameter segments render as input controls.
  // Default is read-only text — opt in by passing `true`.
  editable = input<boolean>(false);

  // The Coworker can only open where it has a context, so the container
  // decides whether the hand-off is offered at all.
  coworkerAvailable = input<boolean>(false);

  feedbackSent = output<NlsFeedback>();
  clearFiltersClicked = output<void>();
  refineWithCoworkerClicked = output<void>();
  attributeUpdated = output<AttributeValueWithParameterKey>();
  simpleValueUpdated = output<SimpleValueWithParameterKey>();

  attributeCategoryDefinitions = computed(() =>
    this.store.filterGlobalStore.attributeCategoryDefinitions(),
  );

  overviewLines = computed(() =>
    buildNlsOverviewView(this.overview(), this.attributeCategoryDefinitions()),
  );

  // Every interactive parameter segment is one filter the AI applied.
  appliedFilterCount = computed(() =>
    this.overviewLines().reduce(
      (count, line) =>
        line.kind === 'interactive'
          ? count +
            line.segments.filter((segment) => segment.kind === 'parameter')
              .length
          : count,
      0,
    ),
  );

  onUpdateAttributeValue(
    updatedAttribute: AttributeDataPayload,
    parameterKey: string,
  ): void {
    this.attributeUpdated.emit({ attribute: updatedAttribute, parameterKey });
  }

  onUpdateSimpleValue(value: string, parameterKey: string): void {
    this.simpleValueUpdated.emit({ value, parameterKey });
  }

  /** Route a picked option back onto the attribute the parameter maps to. */
  onSelectNode(
    node: BDTreeNode | undefined,
    segment: NlsOverviewSegmentView,
  ): void {
    if (!node || segment.kind !== 'parameter') {
      return;
    }
    const { parameter } = segment;
    if (!('attribute' in parameter)) {
      return;
    }
    const updatedAttribute = buildNlsAttributeUpdate(parameter.attribute, node);
    if (updatedAttribute) {
      this.onUpdateAttributeValue(updatedAttribute, parameter.parameterKey);
    }
  }

  sendFeedback(isPositive: boolean): void {
    const score = isPositive ? 1 : 0;
    this.store.recordFeedbackScore(score);
    this.feedbackService.score({
      id: this.store.uniqueScoreId(),
      traceParent: this.store.nlsTraceParent(),
      value: score,
    });

    if (isPositive) {
      this.store.closeCommentBox();
    } else {
      this.store.toggleCommentBox();
    }

    const userId = this.store.authUserId();
    if (userId) {
      this.feedbackSent.emit({
        isPositive,
        resultsCount: this.store.totalSearchResultsCount(),
        userId,
      });
    }
  }

  clearFilters(): void {
    this.clearFiltersClicked.emit();

    const userId = this.store.authUserId();
    if (userId) {
      this.feedbackSent.emit({
        isPositive: false,
        resultsCount: this.store.totalSearchResultsCount(),
        userId,
      });
    }
  }

  submitFeedbackComment(): void {
    const comment = this.store.feedbackComment();
    if (isNotBlank(comment)) {
      this.feedbackService.score({
        id: this.store.uniqueScoreId(),
        traceParent: this.store.nlsTraceParent(),
        value: this.store.feedbackScore() ?? 0,
        comment,
      });
      const userId = this.store.authUserId();
      if (userId) {
        this.feedbackSent.emit({
          isPositive: false,
          resultsCount: this.store.totalSearchResultsCount(),
          userId,
          comment,
        });
      }
    }
    this.store.closeCommentBox();
  }
}
